import Slide from '@/components/shared/Slide';
import GoNoGoBadge from '@/components/shared/GoNoGoBadge';
import { EntryPathChip, ObjectiveChip } from '@/components/shared/Chip';
import { useDisplayOptions } from '@/lib/display-options';
import type {
  GrantProgram,
  GoNoGo,
  ViewMode,
} from '@/types/roadmap.types';

interface Props {
  programs: GrantProgram[];
  mode: ViewMode;
}

const PRIORITY_LANES: { key: GoNoGo; title: string; description: string }[] = [
  { key: 'primary', title: 'Primary', description: 'Highest priority — submit first' },
  { key: 'go', title: 'Go', description: 'Submit applications immediately' },
  { key: 'conditional', title: 'Prepare', description: 'Begin prerequisites, apply when ready' },
  { key: 'monitor', title: 'Monitor', description: 'Track for future opportunities' },
  { key: 'no_go', title: 'Not Pursuing', description: 'Reviewed and set aside' },
];

interface PrioritySequenceSlideProps extends Props {
  laneKeys: readonly GoNoGo[];
  eyebrow: string;
  title: string;
}

function PrioritySequenceSlide({
  programs,
  mode,
  laneKeys,
  eyebrow,
  title,
}: PrioritySequenceSlideProps) {
  const { showObjective, showEntryPath } = useDisplayOptions();
  const laneSet = new Set<GoNoGo>(laneKeys);
  const programsByDecision = PRIORITY_LANES.filter((lane) => laneSet.has(lane.key))
    .map((lane) => ({
      ...lane,
      programs: [...programs.filter((p) => p.go_no_go === lane.key)]
        .sort((a, b) => (b.score?.composite ?? 0) - (a.score?.composite ?? 0)),
    }))
    .filter((lane) => lane.programs.length > 0);

  return (
    <Slide mode={mode}>
      <div className="w-full mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          {eyebrow}
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-6 print:mb-4 print:text-2xl">
          {title}
        </h2>

        <div className="space-y-4 print:space-y-3">
          {programsByDecision.map((lane) => (
            <div key={lane.key} className="rounded-lg border border-border/50 p-4 print:p-3">
              <div className="flex items-center gap-2 mb-3 print:mb-2">
                <GoNoGoBadge value={lane.key} />
                <span className="text-xs text-muted-foreground print:text-[8pt]">{lane.description}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 print:gap-2">
                {lane.programs.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg border border-border/40 bg-muted/10 p-3 print:p-2.5 flex flex-col gap-1.5"
                  >
                    {(showObjective && p.investment_layer && p.investment_layer !== 'general') ||
                    (showEntryPath && p.entry_path) ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {showObjective && p.investment_layer && p.investment_layer !== 'general' && (
                          <ObjectiveChip value={p.investment_layer} />
                        )}
                        {showEntryPath && <EntryPathChip value={p.entry_path} />}
                      </div>
                    ) : null}
                    <p className="text-sm font-medium text-foreground leading-snug print:text-[9pt]">
                      {p.program_name}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed print:text-[8pt]">
                      {p.recommended_next_step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

/** Immediate applications only (Go Now). */
export default function TimelineSlide({ programs, mode }: Props) {
  return (
    <PrioritySequenceSlide
      programs={programs}
      mode={mode}
      laneKeys={['primary', 'go']}
      eyebrow="Strategic Sequence"
      title="Action Priority"
    />
  );
}

export const DEFERRED_GO_NO_GO: readonly GoNoGo[] = ['conditional', 'monitor', 'no_go'];

export function hasDeferredPriorityPrograms(programs: GrantProgram[]): boolean {
  return programs.some((p) => DEFERRED_GO_NO_GO.includes(p.go_no_go));
}

/** Prepare, Monitor, and Pass programs on a separate slide. */
export function DeferredPrioritySlide({ programs, mode }: Props) {
  return (
    <PrioritySequenceSlide
      programs={programs}
      mode={mode}
      laneKeys={DEFERRED_GO_NO_GO}
      eyebrow="Strategic Sequence"
      title="Prepare, Monitor & Pass"
    />
  );
}
